import { BellFilledIcon, CalendarIcon, ErrorMessage, FileIcon, FileOutlineIcon, TagIcon, style } from "@r360/library";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { RentalDesktop } from "../../assets/reservationTypes/Rental";
import { NoSearchResult } from "../../components/NoSearchResult/NoSearchResult";
import { ProductCard } from "../../components/ProductCard/ProductCard";
import { ProductCardSkeleton } from "../../components/ProductCard/ProductCardSkeleton";
import { ReservationTypeHeader } from "../../components/ReservationTypeHeader/ReservationTypeHeader";
import { ReservationTypeImage } from "../../components/ReservationTypeImage/ReservationTypeImage";
import { formatPrice, getCanonicalLink, getDynamicPath } from "../../Helper";
import { newFilterGroupItems } from "../../hooks";
import useAppSelector from "../../hooks/useAppSelector";
import useChangeFilter from "../../hooks/useChangeFilter";
import useChangeSearch from "../../hooks/useChangeSearch";
import useTranslate from "../../hooks/useTranslate";
import {
    currencySelector,
    externalProductsSelector,
    reservationTypeResultSelector,
    resvTypeByLid,
} from "../../selectors/Selectors";
import { TCriteriaItem, TGroup, TItem, TReservationType, TReservationTypeLid } from "../../store/types";

import { ExternalProducts } from "../../components/ExternalProducts";
import { useProductsNotAddedPath } from "../../hooks/useProductsNotAddedPath";
import { requestLoadingByResvTypeSelector } from "../../Selectors";
import { RootState } from "../..";
import { Helmet } from "react-helmet";
import { usePathBuilder } from "../../hooks/usePathBuilder";

type TBikeRentalPageProps = {
    reservationTypeid: TReservationTypeLid;
};

export const BikeRentalPage = ({ reservationTypeid }: TBikeRentalPageProps) => {
    const t = useTranslate();
    const isLoading = useAppSelector(state =>
        requestLoadingByResvTypeSelector(state, "fetchReservationTypeResults", reservationTypeid)
    );
    const { isDesktop } = useAppSelector(state => state.window);
    const currency = useAppSelector(currencySelector);
    const reservationType: TReservationType | undefined = useAppSelector(state =>
        resvTypeByLid(state, reservationTypeid)
    );
    const { buildPath } = usePathBuilder();
    const rentalResult = useAppSelector(state => reservationTypeResultSelector(state, reservationTypeid));
    const reservationTypeCriterias = reservationType?.criterias || {}; // eslint-disable-line
    const externalProductsPosition = reservationType?.externalProductsPosition;
    const externalProducts = useAppSelector(externalProductsSelector)?.filter(
        product => product.reservation_type_id === reservationTypeid
    );
    const channellidFromSearch = rentalResult?.search?.channellid;
    const filteredExternalProducts = externalProducts?.filter(
        product => product.channellid === 0 || product.channellid === channellidFromSearch
    );
    const filter = useAppSelector(state => state.filter);
    const activeFilters = filter[reservationTypeid];
    const clientData = useAppSelector((state: RootState) => state.clientData);

    const [filteredData, setFilteredData] = useState<TGroup[]>([]);
    const [addToCartDataQuantity, setAddToCartDataQuantity] = useState<{ [key: number]: number }>({});
    const addToCartTotalQuantity = Object.values(addToCartDataQuantity).reduce((acc, item) => acc + item, 0);
    const showProductsNotAddedWarning = !!addToCartTotalQuantity;

    const seoTitle =
        clientData.reservationtypes.find((resType: TReservationType) => resType.id === reservationTypeid).seoTitle ||
        clientData.reservationtypes.find((resType: TReservationType) => resType.id === reservationTypeid).description;
    const seoDescription = clientData.reservationtypes.find(
        (resType: TReservationType) => resType.id === reservationTypeid
    ).seoDescription;
    const titleSuffix = clientData?.siteMeta?.seoTitleSuffix || "";
    const seoImage = clientData.reservationtypes
        .find((resType: TReservationType) => resType.id === reservationTypeid)
        .desktopImage?.path?.replace("_org", "_800w");
    const canonicalLink = reservationTypeid && getCanonicalLink(buildPath(reservationTypeid));

    useChangeSearch({
        reservationResultSearch: rentalResult?.search,
        reservationTypeId: reservationTypeid,
        skipGuestsOnSearchWithPrice: true,
        allowSearchWithoutPrice: false,
    });

    useChangeFilter(reservationTypeid);
    useProductsNotAddedPath(showProductsNotAddedWarning);

    useEffect(() => {
        const criterias = Object.values(reservationTypeCriterias)
            .flat()
            .reduce((acc, curr) => {
                acc[curr.code] = curr;
                return acc;
            }, {} as { [key: number]: TCriteriaItem });

        const filteredData = Object.values(rentalResult?.groups || {})
            .map((group: TGroup) => {
                return newFilterGroupItems(group, activeFilters || {}, criterias);
            })
            .map((group: TGroup) => ({
                ...group,
                items: group.items.filter(item => item.available),
            }))
            .filter(group => group.items.length > 0);

        setFilteredData(filteredData);
    }, [rentalResult?.groups, activeFilters, reservationTypeCriterias]);

    // Clear addToCartData when a new search has been made.
    useEffect(() => {
        setAddToCartDataQuantity({});
    }, [rentalResult?.resultSetId]);

    const getItemColumns = (item: TItem) => {
        return [
            {
                title: (
                    <span>
                        {t("book.general.number_days", [
                            `${moment(rentalResult?.search.depdate)
                                .add(1, "days")
                                .diff(moment(rentalResult?.search.arrdate), "days")
                                .toString()}`,
                        ])}
                    </span>
                ),
                icon: <CalendarIcon color={style.brandBlueColor} />,
            },
            {
                title: <span>{item.title}</span>,
                icon: <FileOutlineIcon color={style.brandBlueColor} />,
            },
            {
                title: <span>{t("book.activity.number_left", [item.available ?? 0])}</span>,
                icon: <BellFilledIcon color={style.brandBlueColor} />,
            },
            {
                title: <span>{`${formatPrice(item.price, currency)}`}</span>,
                icon: <TagIcon color={style.brandBlueColor} />,
            },
        ];
    };

    if (!reservationType) {
        return null;
    }

    const getAdditionalInformation = (group: TGroup) => {
        if (!group.groupitem?.category) {
            return null;
        }

        return (
            <div className="u-d-flex u-gap-6 u-align-items-center">
                <FileIcon />
                <span className="u-fw-medium">{group.groupitem.category}</span>
            </div>
        );
    };

    const renderBikeRentalList = () => {
        if (isLoading) {
            return [...Array(8)].map((_, i) => <ProductCardSkeleton key={i} isDesktop={isDesktop} />);
        }

        if (filteredData?.length && !isLoading) {
            return filteredData?.map((group, i) => {
                const title = group.groupitem?.title ?? group.items[0]?.title;
                const images = [
                    ...(group.items[0]?.mainimage ? [group.items[0]?.mainimage] : []),
                    ...(group.items[0]?.images || []),
                ];

                return (
                    <ProductCard
                        key={group.id}
                        group={group}
                        title={title}
                        description={group.groupitem?.weblong ?? group.items[0]?.weblong}
                        images={images}
                        currency={currency}
                        getItemColumns={getItemColumns}
                        showItemsButtonText={t("book.general.show_items")}
                        renderAdditionalInformation={getAdditionalInformation}
                        fallbackImage={getDynamicPath(RentalDesktop)}
                        onAddToCartQuantityChange={data =>
                            setAddToCartDataQuantity(prevState => {
                                if (prevState[group.items[0]?.id] === data) {
                                    return prevState;
                                }

                                return { ...prevState, [group.items[0]?.id]: data };
                            })
                        }
                        decorateItemBeforeCart={item => ({ ...item, groupTitle: title })}
                        reservationType={reservationType?.type}
                    />
                );
            });
        }

        if (
            (rentalResult?.hitsMatchingFilter === "0" || !filteredData?.length) &&
            !isLoading &&
            filteredExternalProducts.length === 0
        ) {
            return <NoSearchResult />;
        }

        if (rentalResult?.error && !isLoading) {
            return (
                <div className="u-pt-24">
                    <ErrorMessage
                        headingText={t("book.general.error")}
                        primaryButtonText={t("book.general.reload_page")}
                        primaryButtonOnClick={() => window.location.reload()}
                    />
                </div>
            );
        }
    };

    const showFilter =
        rentalResult?.groups &&
        reservationTypeCriterias &&
        Object.values(rentalResult.groups).length > 0 &&
        Object.values(reservationTypeCriterias).flat().length > 0;

    return rentalResult?.search || isLoading ? (
        <section>
            <Helmet>
                <title>
                    {seoTitle} | {titleSuffix}
                </title>
                <meta name="description" content={seoDescription} />
                <meta property="og:title" content={`${seoTitle} | ${titleSuffix}`} />
                <meta property="og:image" content={seoImage} />
                <meta property="og:description" content={seoDescription} />
                <link rel="canonical" href={canonicalLink.toString()} />
            </Helmet>
            <ReservationTypeHeader
                reservationTypeId={reservationTypeid}
                hits={filteredData.length + filteredExternalProducts.length}
                filtersAvailable={showFilter}
            />
            {externalProductsPosition === "first" && (
                <ExternalProducts
                    show={!isLoading}
                    reservationTypeId={reservationTypeid}
                    externalProducts={filteredExternalProducts}
                    reservationResult={rentalResult}
                />
            )}
            {renderBikeRentalList()}
            {externalProductsPosition === "last" && (
                <ExternalProducts
                    show={!isLoading}
                    reservationTypeId={reservationTypeid}
                    externalProducts={filteredExternalProducts}
                    reservationResult={rentalResult}
                />
            )}
        </section>
    ) : (
        <>
            <Helmet>
                <title>
                    {seoTitle} | {titleSuffix}
                </title>
                <meta name="description" content={seoDescription} />
                <meta property="og:title" content={`${seoTitle} | ${titleSuffix}`} />
                <meta property="og:image" content={seoImage} />
                <meta property="og:description" content={seoDescription} />
                <link rel="canonical" href={canonicalLink.toString()} />
            </Helmet>
            <ReservationTypeImage reservationTypeId={reservationTypeid} />
        </>
    );
};
